import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import VoteButtons from "@/components/VoteButtons";
import ShareButtons from "@/components/ShareButtons";
import CommentSection from "@/components/CommentSection";

function timeAgo(iso) {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function ThreadDetail() {
  const { id } = useParams();
  const [thread, setThread] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/community/threads/${id}`)
      .then(({ data }) => setThread(data))
      .catch(() => setNotFound(true));
  }, [id]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-[#060810] flex flex-col items-center justify-center gap-4">
        <p className="text-slate-400">Thread not found or awaiting approval.</p>
        <Link to="/community" className="text-cyan-400">Back to Forum</Link>
      </div>
    );
  }

  if (!thread) {
    return <div className="min-h-screen bg-[#060810] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-cyan-400" /></div>;
  }

  return (
    <div className="min-h-screen bg-[#060810]">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
        <Link to="/community" data-testid="thread-back" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-cyan-400 mb-8">
          <ArrowLeft className="w-4 h-4" /> Back to Forum
        </Link>

        {thread.status !== "approved" && (
          <div className="glass rounded-xl p-3 mb-6 text-sm text-amber-400" data-testid="thread-status-banner">
            This thread is <span className="font-bold uppercase">{thread.status}</span> and not yet public.
          </div>
        )}

        <div className="glass rounded-2xl p-6 flex gap-4">
          <VoteButtons type="thread" item={thread} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[10px] font-mono-caption uppercase tracking-widest px-2 py-0.5 rounded-full glass border border-fuchsia-400/30 text-fuchsia-400">{thread.category}</span>
              <span className="text-xs text-slate-500">{thread.author_name} · {timeAgo(thread.created_at)}</span>
            </div>
            <h1 data-testid="thread-title" className="font-heading font-extrabold text-white text-3xl leading-tight mb-3">{thread.title}</h1>
            {thread.body && <p className="text-slate-300 whitespace-pre-wrap leading-relaxed">{thread.body}</p>}
            <div className="mt-5 pt-4 border-t border-[#232A4D]">
              <ShareButtons title={thread.title} path={`/community/${thread.id}`} />
            </div>
          </div>
        </div>

        <CommentSection parentType="thread" parentId={thread.id} />
      </div>
    </div>
  );
}
